<script setup lang="ts">
/**
 * One document, rendered the same way everywhere it is listed.
 *
 * There were three of these. The document browser (`BrowserItemList`) showed a file-type badge,
 * processing status and a relative date; the case hub reimplemented a badge-plus-date subset with no
 * status; the client detail page and the context rail drew a generic `FileText` with no badge, no
 * status and — on the client page — no date at all. So the same file appeared as three different
 * objects depending on which panel you were looking at, and its recency was formatted three ways
 * ("2d", "Jul 31, 2026", nothing) for one underlying `updated_at`.
 *
 * This is the one row. `BrowserItemList` keeps its own markup because it also carries selection,
 * drag-and-drop, expansion and a kebab menu — but it now shares `DocumentStatusChip` with this, which
 * is the part that was actually load-bearing and actually missing elsewhere.
 */
import { FileText } from 'lucide-vue-next'
import { NuxtLink } from '#components'
import { fileExt, extBadgeClass } from '~/lib/fileType'
import { formatRelativeTimeCompact } from '~/utils/relativeTime'
import DocumentStatusChip, { type DocumentStatusFields } from '~/components/documents/DocumentStatusChip.vue'
import { isUnsettled } from '~/lib/documentStatus'
import type { ItemRow } from '~~/shared/items/itemRow'

/**
 * Structural, not `Pick<ItemRow, …>`: the listings that need this row do not all come from `items`.
 * The client rollup is its own projection, and typing against `ItemRow` would have excluded the one
 * surface whose rows were worst.
 */
export interface DocumentRowFields extends DocumentStatusFields, Pick<ItemRow, 'id' | 'name'> {
  file_name?: ItemRow['file_name']
  updated_at?: ItemRow['updated_at'] | null
}

const props = withDefaults(
  defineProps<{
    document: DocumentRowFields
    /** Where the row navigates. Omit for a non-navigating row. */
    to?: string
    /** A second line under the name — the case a rolled-up document belongs to, say. */
    subtitle?: string | null
    /** The context rail's 288px column: smaller type, tighter padding, no date. */
    dense?: boolean
  }>(),
  { dense: false },
)

const { locale } = useI18n()

const ext = computed(() => fileExt(props.document.file_name))
/** A document that is still processing or blocked has nothing useful to say about "updated". */
const unsettled = computed(() => isUnsettled(props.document))
</script>

<template>
  <component
    :is="to ? NuxtLink : 'div'"
    :to="to"
    class="flex items-center rounded-lg transition-colors"
    :class="dense
      ? '-mx-2 gap-2 px-2 py-1.5 text-[13px] hover:bg-card'
      : 'gap-2 px-4 py-2.5 text-sm hover:bg-accent-lightest'"
  >
    <!-- The extension badge is the fastest identifier in the list — a contract, a scan and a
         spreadsheet are three different kinds of work, and a uniform page glyph says none of that. -->
    <span
      v-if="ext"
      class="inline-flex shrink-0 items-center justify-center rounded-md font-bold"
      :class="[dense ? 'h-4 w-7 text-[8px]' : 'h-5 w-9 text-[9px]', extBadgeClass(ext)]"
    >{{ ext }}</span>
    <FileText v-else class="h-4 w-4 shrink-0 text-muted-foreground" />

    <span data-ph-mask class="min-w-0 flex-1">
      <span class="block truncate" :title="document.name">{{ document.name }}</span>
      <span v-if="subtitle" class="block truncate text-xs text-muted-foreground">{{ subtitle }}</span>
    </span>

    <!-- Status when there is something to say, the date otherwise: one trailing slot, never both,
         never empty. -->
    <DocumentStatusChip v-if="unsettled" :item="document" class="shrink-0" />
    <span v-else-if="!dense && document.updated_at" class="shrink-0 text-xs text-muted-foreground">
      {{ formatRelativeTimeCompact(document.updated_at, locale) }}
    </span>

    <!-- Row actions (MAR-174). A slot rather than a built-in menu, because this component is the
         presentational row and the surfaces around it differ in what an action means: only a case
         surface can offer "remove from expediente". Every listing that shows documents should fill
         it — a row with no way out is the bug this slot exists for. -->
    <slot name="actions" />
  </component>
</template>
